import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { FadeUp, MaskedLine, Eyebrow, ParallaxImg } from "@/components/Reveal";

const KHALID_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_elite-transfer-italy/artifacts/mzv0f68v_IMG-20260521-WA0041%281%29.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDER = "[DA INSERIRE]";

export default function Contatti() {
  const [contatti, setContatti] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/contatti`)
      .then((res) => setContatti(res.data))
      .catch(() => setContatti({ configurato: false }));
  }, []);

  const email = contatti?.email || null;
  const telefono = contatti?.telefono || null;
  const waLink = contatti?.whatsapp_link || null;
  const ok = contatti?.configurato || false;

  return (
    <>
      <SEO
        title="Contatti | Zaichauffer – Chauffeur Privato Italia"
        description="Contatta Zaichauffer per informazioni su un servizio di autista privato: trasferimenti aeroportuali, business ed eventi a Milano, in Lombardia, Emilia-Romagna e in tutta Italia."
      />

      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-20 pt-40 md:pb-28 md:pt-52" data-testid="contatti-hero">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <FadeUp>
          <Eyebrow>Contatti</Eyebrow>
        </FadeUp>
        <h1 className="mt-10 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
          <MaskedLine>PARLIAMO DEL TUO PROSSIMO VIAGGIO.</MaskedLine>
        </h1>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-xl text-[15px] font-light leading-relaxed text-mute">
            Per informazioni, disponibilità o richieste personalizzate puoi
            contattare direttamente Zaichauffer.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-36" data-testid="contatti-details">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <div className="border border-white/10 bg-coal p-10 md:p-14">
                <p className="label-cap">Il titolare</p>
                <p className="mt-4 font-serif text-3xl text-bone">
                  Khalid El Aissaoui
                </p>

                <div className="mt-12 space-y-8">
                  <div>
                    <p className="label-cap !text-[9px]">Email</p>
                    <p data-testid="contatti-email-value" className="mt-2 text-[15px] font-light text-bone/85">
                      {email || PLACEHOLDER}
                    </p>
                  </div>
                  <div>
                    <p className="label-cap !text-[9px]">Telefono</p>
                    <p data-testid="contatti-phone-value" className="mt-2 text-[15px] font-light text-bone/85">
                      {telefono || PLACEHOLDER}
                    </p>
                  </div>
                  <div>
                    <p className="label-cap !text-[9px]">WhatsApp</p>
                    <p data-testid="contatti-whatsapp-value" className="mt-2 text-[15px] font-light text-bone/85">
                      {waLink ? "Disponibile" : PLACEHOLDER}
                    </p>
                  </div>
                </div>

                <div className="mt-14 flex flex-col gap-4">
                  {telefono ? (
                    <a data-testid="contatti-button-chiama" href={`tel:${telefono.replace(/\s/g, "")}`} className="btn-solid">
                      <Phone size={15} /> Chiama
                    </a>
                  ) : (
                    <button data-testid="contatti-button-chiama" disabled className="btn-disabled">
                      <Phone size={15} /> Chiama
                    </button>
                  )}

                  {waLink ? (
                    <a data-testid="contatti-button-whatsapp" href={waLink} target="_blank" rel="noopener noreferrer" className="btn-outline btn-wa">
                      <span className="wa-ic"><WhatsAppIcon size={15} /></span> Scrivi su WhatsApp
                    </a>
                  ) : (
                    <button data-testid="contatti-button-whatsapp" disabled className="btn-disabled">
                      <span className="wa-ic"><WhatsAppIcon size={15} /></span> Scrivi su WhatsApp
                    </button>
                  )}

                  {email ? (
                    <a data-testid="contatti-button-email" href={`mailto:${email}`} className="btn-outline">
                      <Mail size={15} /> Invia email
                    </a>
                  ) : (
                    <button data-testid="contatti-button-email" disabled className="btn-disabled">
                      <Mail size={15} /> Invia email
                    </button>
                  )}
                </div>

                {!ok && (
                  <p className="mt-10 text-xs font-light leading-relaxed text-mute/70">
                    I recapiti sono in fase di configurazione: nel frattempo
                    puoi usare il modulo di richiesta preventivo, la tua
                    richiesta arriverà direttamente a Khalid.
                  </p>
                )}
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-6">
            <FadeUp delay={0.15} className="lg:pl-12">
              <div className="frame-gold">
                <ParallaxImg
                  src={KHALID_IMG}
                  alt="Khalid El Aissaoui con il veicolo nero di Zaichauffer"
                  className="relative z-10 aspect-[4/5] w-full"
                />
              </div>
              <Link to="/richiedi-preventivo" className="lux-link mt-14 inline-flex group">
                Richiedi un preventivo
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
